import React, { useState } from 'react';
import { Mail, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Input } from '../ui';

interface ForgotPasswordFormProps {
  onBack: () => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { requestPasswordReset } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;
    setError('');

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      await requestPasswordReset(email);
      setEmailSent(true);
    } catch {
      setEmailSent(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (emailSent) {
    return (
      <div className="w-full">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-7 h-7 text-green-600" />
          </div>
          <h2 className="text-[22px] sm:text-[24px] font-semibold text-primary-900 mb-1.5 tracking-tight">
            Check your email
          </h2>
          <p className="text-[13px] sm:text-sm text-neutral-500 leading-relaxed">
            If an account with <span className="font-medium text-neutral-700">{email}</span> exists, you'll receive a reset link.
          </p>
        </div>

        <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-4 mb-6">
          <p className="text-[13px] text-neutral-600 text-center leading-relaxed">
            <span className="font-medium">Didn't receive it?</span> Check your spam folder. The link expires in 1 hour.
          </p>
        </div>

        <Button
          id="back-to-sign-in-btn"
          onClick={onBack}
          fullWidth
          size="lg"
          leftIcon={<ArrowLeft className="w-4 h-4" />}
        >
          Back to sign in
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Button
        onClick={onBack}
        variant="link"
        leftIcon={<ArrowLeft className="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-0.5" />}
        className="text-[13px] mb-6"
      >
        Back
      </Button>

      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-[22px] sm:text-[24px] font-semibold text-primary-900 mb-1.5 tracking-tight">
          Reset password
        </h2>
        <p className="text-[13px] sm:text-sm text-neutral-500">
          Enter your email to receive a reset link
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
        <Input
          id="email"
          type="email"
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isSubmitting}
          placeholder="you@company.com"
          autoComplete="email"
          autoFocus
          fullWidth
          inputSize="lg"
          leftIcon={<Mail className="w-4 h-4" />}
        />

        {error && (
          <div className="flex items-center gap-2.5 p-3.5 bg-red-50 border border-red-100 rounded-xl">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-[13px] text-red-600">{error}</p>
          </div>
        )}

        <Button
          id="reset-password-btn"
          type="submit"
          loading={isSubmitting}
          fullWidth
          size="lg"
          className="mt-2"
        >
          {isSubmitting ? 'Sending...' : 'Send reset link'}
        </Button>
      </form>
    </div>
  );
};
